import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { StateInterface } from 'Client/redux/types';
import isClient, { cookiesEnabled } from 'Utils/environment';
import { ShopToClubConfigState } from 'AppShell/appshell_types/remoteConfigType';
import useConfig from 'AppShell/hooks/useConfig';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import useWasClubRegistrationViewed from 'Client/hooks/flags/useWasClubRegistrationViewed';
import { ShopToClubShownLabel } from 'Common/ga/shopToClub';
import useRouter from 'AppShell/hooks/useRouter';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { isAnyOverlayActive } from 'Client/redux/header/overlays/selectors';
import { isPsmTraffic } from 'Client/components/Overlays/ShopToClub/helper';
import { EventDispatcher } from 'Common/events';
import { DisplayActions, OverlayIds, PageType } from 'AppShell/appshell_types';

const lockerPageTypes = [
    PageType.HOMEPAGE,
    PageType.PDP,
    PageType.INTERIOR_DESIGN_SERVICE,
    PageType.LOOKS,
    ...ProductListingPages,
];

const useForceClubRegistrationPopup = () => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasClubRegistrationViewed();
    const { isLoggedIn, isRegistered } = useUserTracking();
    const { isAppWebviewPage } = useRouter();
    const config = useConfig();
    const [clicked, setClicked] = useState(false);

    const isAnyActive: boolean = useSelector<StateInterface, boolean>(state =>
        isAnyOverlayActive(state.header.overlays)
    );
    const data = useSelector<StateInterface, ShopToClubConfigState>(state => state.configurations.shopToClub);
    const {
        location: { search },
    } = useRouter();
    const { pageType } = useCurrentPageType();
    const { p100OnClickForceClubRegistrationPopup } = useFeatureToggle();
    const isDisabledFromBob = !data?.isEnabled;

    const searchParams = new URLSearchParams(search);
    const canShowOnThisPage = lockerPageTypes.includes(pageType) && isPsmTraffic(searchParams);

    const failConditions = [
        isRegistered,
        isAppWebviewPage,
        isLoggedIn,
        isDisabledFromBob,
        isAnyActive,
        !p100OnClickForceClubRegistrationPopup,
        !canShowOnThisPage,
        !isClient(),
    ];

    const skipRender = failConditions.some(Boolean);

    const onPageBlockerClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setClicked(true);
    };

    useEffect(() => {
        const expirationInDays = data && data.expirationInDays;
        const cookieTTL = expirationInDays * 24 * 60 * 60 * 1000;
        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (!skipRender && clicked) {
            const eventDispatcher = EventDispatcher();
            eventDispatcher.updateOverlay(DisplayActions.SHOW, config.shopToClubOverlayId as OverlayIds, {
                isLogin: false,
                isDismissible: false,
                isUndismissibleFromOutsideClick: true,
                trackingLabel: ShopToClubShownLabel.Default,
            });
            setClicked(false);
            if (cookieTTL) {
                setHasBeenViewedStatus(1, cookieTTL);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canShowOnThisPage, clicked]);

    return {
        shouldRenderBlocker: !skipRender,
        onPageBlockerClick,
    };
};

export default useForceClubRegistrationPopup;
